import {
  Box,
  InputAdornment,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import { Delete, Save, Check, Refresh } from "@material-ui/icons";
import { AR, MX } from "country-flag-icons/react/3x2";

import {
  AsYouType,
  CountryCode,
  getExampleNumber,
  isValidPhoneNumber,
} from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";
import { useCallback, useEffect, useState } from "react";

type PhoneEntryProps = {
  number: string;
  verfied: boolean;
  handleVerify: (code: string) => void;
  handleDelete: () => void;
  handleAdd: (number: string) => void;
  handleResendCode: () => void;
};

export default function PhoneEntry({
  number,
  verfied,
  handleVerify,
  handleDelete,
  handleAdd,
  handleResendCode,
}: PhoneEntryProps): JSX.Element {
  const [numberState, setNumber] = useState<string>(number ?? "");
  const [country, setCountry] = useState<string>("AR");
  const [numberError, setNumberError] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");

  const initNumber = useCallback(
    (number: string) => {
      if (!number) {
        setNumber("");
        return;
      }
      const [country, prefix] = getCountryPrefixFromNumber(number);
      setCountry(country);
      setNumber(number.replace(prefix, ""));
    },
    [number],
  );

  useEffect(() => {
    initNumber(number);
  }, [number, initNumber]);

  const handleNumberSave = (): void => {
    try {
      handleAdd?.(getCountryPrefix(country) + numberState);
    } catch (e) {}
  };
  const handleNumberDelete = (): void => {
    try {
      handleDelete?.();
    } catch (e) {}
  };

  const handleNumberVerify = (): void => {
    try {
      console.log(code);
      handleVerify?.(code);
    } catch (e) {}
  };

  function generatePhoneNumberError(country?: CountryCode): string {
    let message = "El número de teléfono debe ser móvil para WhatsApp";
    if (!country) {
      return message;
    }
    if (country === "AR") {
      message = `El número de teléfono debe ser un móvil de Argentina Ejemplo. +54 9 261 512 3456`;
    } else {
      const exampleNumber = getExampleNumber(country, examples)?.format(
        "E.164",
      );

      message = exampleNumber
        ? `El número de teléfono debe ser móvil para WhatsApp. Ejemplo: ${exampleNumber}`
        : message;
    }
    return message;
  }

  const handleChangeNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const fullNumber = getCountryPrefix(country) + value;

    // Use AsYouType for formatting and validation
    const ayt = new AsYouType(country as any);
    const formatted = ayt.input(fullNumber);

    // Remove country prefix from formatted number for display
    const displayNumber = formatted
      .replace(getCountryPrefix(country), "")
      .trim();
    setNumber(displayNumber);

    // Validate the complete phone number
    const isValid = isValidPhoneNumber(fullNumber, country as any);
    setNumberError(!isValid && value.length > 0);
  };

  const getCountryPrefix = (country: string): string => {
    switch (country) {
      case "AR":
        return "+54";
      case "MX":
        return "+52";
      default:
        return "+54";
    }
  };

  const getCountryPrefixFromNumber = (number: string): string[] => {
    for (let country of ["AR", "MX"]) {
      const prefix = getCountryPrefix(country);
      if (number.startsWith(prefix)) {
        return [country, prefix];
      }
    }
  };

  return (
    <Box>
      <FormControl fullWidth>
        <TextField
          error={numberError}
          helperText={
            numberError
              ? generatePhoneNumberError(country as CountryCode)
              : undefined
          }
          id="phone-number-input"
          label="Phone Number"
          value={numberState}
          disabled={!!number}
          onChange={handleChangeNumber}
          variant="outlined"
          margin="normal"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Select
                  value={country}
                  onChange={(
                    event: React.ChangeEvent<{
                      name?: string;
                      value: unknown;
                    }>,
                  ) => setCountry(event.target.value as string)}
                  disableUnderline
                  disabled={!!number}
                  style={{
                    marginRight: 8,
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        width: 60,
                      },
                    },
                  }}
                >
                  <MenuItem value={"AR"}>
                    <AR style={{ width: 24, height: 16 }} title="AR" />
                  </MenuItem>
                  <MenuItem value={"MX"}>
                    <MX style={{ width: 24, height: 16 }} title="MX" />
                  </MenuItem>
                </Select>
                <span style={{ marginLeft: 4, color: "rgba(0, 0, 0, 0.87)" }}>
                  {getCountryPrefix(country)}
                </span>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {!!number && (
                  <IconButton
                    aria-label="delete"
                    onClick={handleNumberDelete}
                    edge="end"
                  >
                    <Delete />
                  </IconButton>
                )}
                {!number && !numberError && numberState && (
                  <IconButton
                    aria-label="save"
                    onClick={handleNumberSave}
                    edge="end"
                    color="primary"
                  >
                    <Save />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
      {number && !verfied && (
        <Box style={{ marginTop: "16px" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <TextField
              style={{ flex: 1 }}
              label="Verification Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code"
              variant="outlined"
              margin="normal"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="verify"
                      onClick={handleNumberVerify}
                      edge="end"
                      color="primary"
                      disabled={!code}
                    >
                      <Check />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Refresh />}
              onClick={handleResendCode}
              style={{
                whiteSpace: "nowrap",
                marginTop: "16px",
              }}
            >
              Resend
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
